import { type ValueSetterParams } from "ag-grid-community";
import { type CustomCellRendererProps } from "ag-grid-react";

import { type Currency } from "@vega-api/generated/types/currency";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { type Money } from "@vega-api/generated/types/money";
import { MoneyAbbreviationCellEditor } from "@vega-core/components/DataGrid/MoneyAbbreviationCellEditor";
import { useColumnToggle } from "@vega-core/components/DataGrid/useColumnToggle";
import { suppressActionsTabKey } from "@vega-core/components/DataGrid/utils";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { formatMoney, formatMoneyShort } from "@vega-core/utils/formatMoney";

import {
  deriveExportColumns,
  type ExportableAnyColDef,
  type ExportableColDef,
  toGridColumns,
} from "../../../hooks/deriveExportColumns";
import { RejectDetailsTooltip } from "../../TrackingTable/components/RejectDetailsTooltip";
import { TrackingRowActionsMenu } from "../../TrackingTable/components/TrackingRowActionsMenu";
import { TrackingTag } from "../../TrackingTable/components/TrackingTag";
import {
  formatAllocationAmountCellText,
  formatCommitmentAmountCellText,
  formatGpEstimatedTicketSizeMaxCellText,
  formatGpEstimatedTicketSizeMinCellText,
  formatGpIndicativeOrderCellText,
  formatIndicativeAmountCellText,
  formatLimitedPartnerCellText,
  formatLpTicketSizeMaxCellText,
  formatLpTicketSizeMinCellText,
  formatProbabilityCellText,
  formatStageCellText,
  formatWeightedOrderCellText,
} from "../../TrackingTable/utils/formatCellContent";

type UseTrackAllocationColumnsParams = {
  dealId: string;
  dealCurrency: Currency;
  canEditDealOpportunity: boolean;
  isMutationLocked: boolean;
};

export const TRACK_ALLOCATION_COL_IDS = {
  limitedPartnerName: "limitedPartnerName",
  stage: "stage",
  tier: "tier",
  nda: "nda",
  lpTicketSizeMin: "lpTicketSizeMin",
  lpTicketSizeMax: "lpTicketSizeMax",
  indicativeAmount: "indicativeAmount",
  estimatedTicketSizeMin: "estimatedTicketSizeMin",
  estimatedTicketSizeMax: "estimatedTicketSizeMax",
  gpIndicativeOrder: "gpIndicativeOrder",
  probability: "probability",
  weightedOrder: "weightedOrder",
  allocationAmount: "allocationAmount",
  commitmentAmount: "commitmentAmount",
  actions: "actions",
} as const;

const MoneyRenderer =
  (
    getMoney: (row: DealTrackingLimitedPartnerBreakdown) => Money | undefined,
    colId: string,
  ) =>
  ({ data }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
    if (!data) return null;
    const money = getMoney(data);

    return (
      <span data-testid={`cell-${data.dealOpportunityId}-${colId}`}>
        {money ? formatMoneyShort(money) : EMPTY_CELL_VALUE}
      </span>
    );
  };

// Totals row stores the summed number under the column id; format it back into
// money using the deal currency (all rows share one currency).
const createMoneyTotalRenderer =
  (colId: string, format: (money: Money) => string, dealCurrency: Currency) =>
  ({ data }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
    const amount = (data as Record<string, unknown> | undefined)?.[colId];

    if (typeof amount !== "number") return null;

    return format({ amount, currency: dealCurrency });
  };

export const useTrackAllocationColumns = ({
  dealId,
  dealCurrency,
  canEditDealOpportunity,
  isMutationLocked,
}: UseTrackAllocationColumnsParams) => {
  const createEditableMoneyColumn = ({
    getMoney,
    setMoney,
    validate,
    isEditable = () => true,
    ...colDef
  }: ExportableColDef<DealTrackingLimitedPartnerBreakdown> & {
    getMoney: (row: DealTrackingLimitedPartnerBreakdown) => Money | undefined;
    setMoney: (row: DealTrackingLimitedPartnerBreakdown, money: Money) => void;
    validate?: (
      value: number | null,
      row: DealTrackingLimitedPartnerBreakdown,
    ) => string[] | null;
    isEditable?: (row: DealTrackingLimitedPartnerBreakdown) => boolean;
  }): ExportableColDef<DealTrackingLimitedPartnerBreakdown> => ({
    width: 140,
    toggleable: true,
    aggregateTotal: true,
    totalCellRenderer: createMoneyTotalRenderer(
      colDef.colId ?? "",
      formatMoneyShort,
      dealCurrency,
    ),
    editable: (params) =>
      !!params.data && canEditDealOpportunity && isEditable(params.data),
    cellEditor: MoneyAbbreviationCellEditor,
    cellEditorParams: (params: {
      data: DealTrackingLimitedPartnerBreakdown;
    }) => ({
      currency: dealCurrency,
      customValidationErrors: validate
        ? (value: number | null) => validate(value, params.data)
        : undefined,
    }),
    valueGetter: (params) =>
      params.data ? (getMoney(params.data)?.amount ?? null) : null,
    valueSetter: (
      params: ValueSetterParams<
        DealTrackingLimitedPartnerBreakdown,
        number | null
      >,
    ) => {
      const newAmount = params.newValue;

      if (newAmount === null || newAmount === undefined) return false;

      const currency = getMoney(params.data)?.currency ?? dealCurrency;
      setMoney(params.data, { amount: newAmount, currency });

      return true;
    },
    cellRenderer: MoneyRenderer(getMoney, colDef.colId ?? ""),
    ...colDef,
  });

  const limitedPartnerNameColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ALLOCATION_COL_IDS.limitedPartnerName,
      headerName: m.coInvestments_tracking_limitedPartner(),
      field: "limitedPartnerName",
      width: 240,
      pinned: "left",
      exportValue: formatLimitedPartnerCellText,
      totalCellRenderer: () => (
        <span data-testid="cell-total-label">{m.common_total()}</span>
      ),
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <span
            className="body-base-medium"
            data-testid={`cell-${data.dealOpportunityId}-limitedPartnerName`}
          >
            {data.limitedPartnerName}
          </span>
        );
      },
    };

  const actionsColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_ALLOCATION_COL_IDS.actions,
    headerName: "",
    width: 50,
    pinned: "left",
    sortable: false,
    suppressKeyboardEvent: suppressActionsTabKey,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <TrackingRowActionsMenu
          dealId={dealId}
          showConfirmNdaSigned={false}
          trackingItem={data}
        />
      );
    },
  };

  const stageColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> = {
    colId: TRACK_ALLOCATION_COL_IDS.stage,
    headerName: m.coInvestments_tracking_stage(),
    width: 140,
    toggleable: true,
    field: "stage",
    exportValue: formatStageCellText,
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      return (
        <RejectDetailsTooltip
          gpRejectReason={data.gpRejectReason}
          gpRejectSubreason={data.gpRejectSubreason}
          rejectComment={data.rejectComment}
          rejectReason={data.rejectReason}
        >
          <span className="body-base-regular">{formatStageCellText(data)}</span>
        </RejectDetailsTooltip>
      );
    },
  };

  const lpTicketSizeMinColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.lpTicketSizeMin,
    headerName: m.coInvestments_tracking_lpMinimumTicketSize(),
    exportValue: formatLpTicketSizeMinCellText,
    getMoney: (row) => row.ticketSize?.minimum,
    setMoney: (row, money) => {
      row.ticketSize = {
        minimum: money,
        maximum: row.ticketSize?.maximum ?? {
          amount: 0,
          currency: dealCurrency,
        },
      };
    },
    validate: (value, row) => {
      const max = row.ticketSize?.maximum.amount;

      if (value !== null && max !== undefined && value > max) {
        return [
          m.coInvestments_submitTicketSize_modal_minInputMustNotExceedMax(),
        ];
      }

      return null;
    },
  });

  const lpTicketSizeMaxColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.lpTicketSizeMax,
    headerName: m.coInvestments_tracking_lpMaximumTicketSize(),
    exportValue: formatLpTicketSizeMaxCellText,
    getMoney: (row) => row.ticketSize?.maximum,
    setMoney: (row, money) => {
      row.ticketSize = {
        minimum: row.ticketSize?.minimum ?? {
          amount: 0,
          currency: dealCurrency,
        },
        maximum: money,
      };
    },
    validate: (value, row) => {
      const min = row.ticketSize?.minimum.amount;

      if (value !== null && min !== undefined && value < min) {
        return [
          m.coInvestments_submitTicketSize_modal_maxInputMustBeAtLeastMin(),
        ];
      }

      return null;
    },
  });

  const indicativeAmountColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.indicativeAmount,
    headerName: m.coInvestments_tracking_indicativeOrder(),
    exportValue: formatIndicativeAmountCellText,
    getMoney: (row) => row.indicativeAmount,
    setMoney: (row, money) => {
      row.indicativeAmount = money;
    },
  });

  const estimatedTicketSizeMinColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.estimatedTicketSizeMin,
    headerName: m.coInvestments_tracking_gpMinimumTicketSize(),
    exportValue: formatGpEstimatedTicketSizeMinCellText,
    getMoney: (row) => row.estimatedTicketSizeMin,
    setMoney: (row, money) => {
      row.estimatedTicketSizeMin = money;
    },
    validate: (value, row) => {
      const max = row.estimatedTicketSizeMax?.amount;

      if (value !== null && max !== undefined && value > max) {
        return [m.coInvestments_gpEstimatedTicketSize_modal_minNotExceedMax()];
      }

      return null;
    },
  });

  const estimatedTicketSizeMaxColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.estimatedTicketSizeMax,
    headerName: m.coInvestments_tracking_gpMaximumTicketSize(),
    exportValue: formatGpEstimatedTicketSizeMaxCellText,
    getMoney: (row) => row.estimatedTicketSizeMax,
    setMoney: (row, money) => {
      row.estimatedTicketSizeMax = money;
    },
    validate: (value, row) => {
      const min = row.estimatedTicketSizeMin?.amount;

      if (value !== null && min !== undefined && value < min) {
        return [m.coInvestments_gpEstimatedTicketSize_modal_maxAtLeastMin()];
      }

      return null;
    },
  });

  const gpIndicativeOrderColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.gpIndicativeOrder,
    headerName: m.coInvestments_tracking_gpIndicativeOrder(),
    exportValue: formatGpIndicativeOrderCellText,
    getMoney: (row) => row.gpIndicativeOrder,
    setMoney: (row, money) => {
      row.gpIndicativeOrder = money;
    },
  });

  const probabilityColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ALLOCATION_COL_IDS.probability,
      headerName: m.coInvestments_tracking_gpProbability(),
      field: "probability",
      width: 120,
      toggleable: true,
      exportValue: formatProbabilityCellText,
      editable: (params) => !!params.data && canEditDealOpportunity,
      cellEditor: "agNumberCellEditor",
      cellEditorParams: { min: 0, max: 100 },
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return data.probability == null
          ? EMPTY_CELL_VALUE
          : `${data.probability}%`;
      },
    };

  const weightedOrderColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ALLOCATION_COL_IDS.weightedOrder,
      headerName: m.coInvestments_tracking_gpWeightedOrder(),
      width: 140,
      toggleable: true,
      aggregateTotal: true,
      exportValue: formatWeightedOrderCellText,
      totalCellRenderer: createMoneyTotalRenderer(
        TRACK_ALLOCATION_COL_IDS.weightedOrder,
        formatMoneyShort,
        dealCurrency,
      ),
      valueGetter: (params) => params.data?.weightedOrder?.amount ?? null,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return data.weightedOrder
          ? formatMoneyShort(data.weightedOrder)
          : EMPTY_CELL_VALUE;
      },
    };

  const allocationAmountColumn = createEditableMoneyColumn({
    colId: TRACK_ALLOCATION_COL_IDS.allocationAmount,
    headerName: m.coInvestments_tracking_allocationTitle(),
    width: 160,
    exportValue: (row) =>
      formatAllocationAmountCellText(row, { forExport: true }).join(""),
    getMoney: (row) => row.allocationAmount,
    setMoney: (row, money) => {
      row.allocationAmount = money;
    },
    isEditable: (row) => !row.commitmentAmount && !row.isRejected,
    totalCellRenderer: createMoneyTotalRenderer(
      TRACK_ALLOCATION_COL_IDS.allocationAmount,
      formatMoney,
      dealCurrency,
    ),
    cellRenderer: ({
      data,
    }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
      if (!data) return null;

      const variant = (() => {
        if (data.isRejected) return "negative";

        if (!data.allocationAmount) return "outline";

        return "positive";
      })();

      return (
        <RejectDetailsTooltip
          gpRejectReason={data.gpRejectReason}
          gpRejectSubreason={data.gpRejectSubreason}
          rejectComment={data.rejectComment}
          rejectReason={data.rejectReason}
        >
          <div className="h-full flex items-center">
            <TrackingTag
              className="flex flex-col gap-0"
              dataTestId={`allocation-amount-${data.limitedPartnerId}`}
              variant={variant}
            >
              {formatAllocationAmountCellText(data)}
            </TrackingTag>
          </div>
        </RejectDetailsTooltip>
      );
    },
  });

  const commitmentAmountColumn: ExportableColDef<DealTrackingLimitedPartnerBreakdown> =
    {
      colId: TRACK_ALLOCATION_COL_IDS.commitmentAmount,
      headerName: m.coInvestments_tracking_commitmentAmount(),
      width: 160,
      toggleable: true,
      aggregateTotal: true,
      exportValue: formatCommitmentAmountCellText,
      totalCellRenderer: createMoneyTotalRenderer(
        TRACK_ALLOCATION_COL_IDS.commitmentAmount,
        formatMoney,
        dealCurrency,
      ),
      valueGetter: (params) => params.data?.commitmentAmount?.amount ?? null,
      cellRenderer: ({
        data,
      }: CustomCellRendererProps<DealTrackingLimitedPartnerBreakdown>) => {
        if (!data) return null;

        return (
          <div className="h-full flex flex-col justify-center">
            <span className="body-base-medium">
              {data.commitmentAmount
                ? formatMoney(data.commitmentAmount)
                : EMPTY_CELL_VALUE}
            </span>
            {data.isClosed ? (
              <TrackingTag
                dataTestId={`closed-tag-${data.limitedPartnerId}`}
                variant="disabled"
              >
                {m.coInvestments_tracking_closed_tag()}
              </TrackingTag>
            ) : null}
          </div>
        );
      },
    };

  const columns: ExportableAnyColDef<DealTrackingLimitedPartnerBreakdown>[] = [
    {
      groupId: "base",
      headerName: "",
      marryChildren: true,
      // Row actions are all mutations (close/decline deal, NDA edits) — drop the
      // column entirely on a closed deal rather than render an empty menu.
      children: [
        limitedPartnerNameColumn,
        ...(isMutationLocked ? [] : [actionsColumn]),
        stageColumn,
      ],
    },
    {
      groupId: "gp",
      headerName: m.coInvestments_tracking_gpGroup(),
      marryChildren: true,
      children: [
        estimatedTicketSizeMinColumn,
        estimatedTicketSizeMaxColumn,
        gpIndicativeOrderColumn,
        probabilityColumn,
        weightedOrderColumn,
      ],
    },
    {
      groupId: "lp",
      headerName: m.coInvestments_tracking_lpGroup(),
      marryChildren: true,
      children: [
        lpTicketSizeMinColumn,
        lpTicketSizeMaxColumn,
        indicativeAmountColumn,
      ],
    },
    {
      groupId: "allocation",
      headerName: "",
      marryChildren: true,
      children: [allocationAmountColumn, commitmentAmountColumn],
    },
  ];

  const { columns: exportColumns, groupedHeaders: exportGroupedHeaders } =
    deriveExportColumns(columns);
  const { columnDefs, renderColumnToggle } = useColumnToggle(
    toGridColumns(columns),
  );

  return {
    columnDefs,
    renderColumnToggle,
    exportColumns,
    exportGroupedHeaders,
  };
};
